import { useTranslation } from "react-i18next";
import {
  Box,
  Heading,
  Text,
  Image,
  SimpleGrid,
  LinkBox,
  LinkOverlay,
  Spinner,
  Flex,
  Button,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";

export default function NewsList() {
  const colors = {
    teal: "#1da179",
    tealLight: "#34c49b",
    grayBg: "#f9f9f9",
  };

  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const fetchNews = async () => {
    try {
      const res = await axios.get("https://actpolymers-spring-26d90d713ec5.herokuapp.com/api/news");
      setNewsList(res.data);
    } catch (err) {
      console.error("Failed to fetch news", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  if (loading) {
    return (
      <Flex
        py={20}
        justify="center"
        align="center"
        minH="60vh"
        bg={colors.grayBg}
      >
        <Spinner size="xl" color={colors.teal} />
      </Flex>
    );
  }

  // Motion wrappers
  const MotionHeading = motion(Heading);
  const MotionLinkBox = motion(LinkBox);
  const MotionText = motion(Text);
  const MotionButton = motion(Button);

  return (
    <Box py={20} px={6} bg={colors.grayBg} minH="80vh">
      {/* Section Header */}
      <MotionHeading
        size="2xl"
        textAlign="center"
        mb={12}
        color={colors.teal}
        fontWeight="bold"
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        {isArabic ? "الأخبار" : "News"}
      </MotionHeading>

      {newsList.length === 0 ? (
        <Flex direction="column" align="center" justify="center" minH="40vh">
          <MotionText
            fontSize="lg"
            color="gray.600"
            mb={6}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            {isArabic
              ? "لا توجد أخبار متاحة حالياً"
              : "There is no news available at the moment."}
          </MotionText>
          <MotionButton
            as={Link}
            to="/"
            bg={colors.teal}
            color="white"
            size="md"
            px={6}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            _hover={{ bg: colors.tealLight }}
          >
            {isArabic ? "العودة للرئيسية" : "Back to main page"}
          </MotionButton>
        </Flex>
      ) : (
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={10}
          maxW="7xl"
          mx="auto"
        >
          {newsList.map((news, idx) => (
            <MotionLinkBox
              key={news.id}
              bg="white"
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              whileHover={{ scale: 1.03 }}
            >
              <Image
                src={news.imageUrl || "/placeholder.jpg"}
                alt={news.title}
                h="220px"
                w="100%"
                objectFit="cover"
              />
              <Box p={6}>
                <Heading
                  size="md"
                  mb={3}
                  color={colors.teal}
                  _hover={{ textDecoration: "underline" }}
                >
                  <LinkOverlay as={Link} to={`/news/${news.id}`}>
                    {news.title}
                  </LinkOverlay>
                </Heading>
                <Text noOfLines={3} color="gray.600" fontSize="sm">
                  {news.shortDescription}
                </Text>
              </Box>
            </MotionLinkBox>
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
}
